import { memo, useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import {
	IconArrowUpRightOutline18 as External,
	IconEyeOutline18 as Eye,
	IconFileContentOutline18 as FileIcon,
	IconGearOutline18 as Gear,
	IconLink3Outline18 as Link3,
	IconArrowRotateClockwiseOutline18 as Sync,
} from "nucleo-ui-outline-18"

import { apiMethodLabel } from "@productlane/lib/src/api-reference/method-label"
import { cn } from "@productlane/lib/src/cn"
import { getPortalUrl } from "@productlane/lib/src/domain"
import { Badge, Button, Checkbox, Loader } from "@productlane/ui"
import { ApiMethodBadge } from "@productlane/ui/src/components/api-reference"

import type { RouterOutputs } from "@/lib/api"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import { MobileBackButton } from "../mobile-back-button"
import { MOBILE_BAR_CLEARANCE } from "../sidebar-geometry"
import { API_MONO_CLASS, API_PANE_HEADER_CLASS } from "./api-reference-env"
import { METHOD_TEXT } from "./api-reference-nav"
import { DocsHeaderTabs } from "./docs-header-tabs"
import { useDocsTabs } from "./use-docs-tabs"
import { TAB_COPY, usePublishDocsTab } from "./use-publish-docs-tab"

type ApiReference = NonNullable<RouterOutputs["apiReference"]["get"]>
type SyncResult = RouterOutputs["apiReference"]["syncNow"]

function useCanEdit() {
	const { role } = useRole()
	return !!role && role !== "VIEWER"
}

function relative(iso: string | null | undefined) {
	return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : "never"
}

export function syncToast(result: SyncResult) {
	switch (result.status) {
		case "unchanged":
			toast.success({ title: "The spec hasn't changed since the last sync" })
			break
		case "published":
			toast.success({ title: `Synced: ${result.summary}` })
			break
		case "held":
			toast.info({
				title: `Held back ${result.held.breakingCount} breaking change${result.held.breakingCount === 1 ? "" : "s"}`,
				description: "Review them below, then publish or dismiss.",
			})
			break
		case "failed":
			toast.error({ title: "Sync failed", description: result.error })
			break
	}
}

export const ApiReferencePage = memo(({ tabId }: { tabId: string }) => {
	const workspace = useZeroWorkspace()
	const { tabs } = useDocsTabs()
	const { setApiSettingsTabId } = useDocsTabDialogs()
	const tab = tabs.find((t) => t.id === tabId)
	const portalSlug =
		workspace?.portalInstances?.find((p) => p.id === tab?.portalInstanceId)
			?.slug ?? null
	const publishTab = usePublishDocsTab()
	const [isPublishing, setIsPublishing] = useState(false)
	const query = api.apiReference.get.useQuery({ tabId })
	const utils = api.useUtils()
	const refresh = () => utils.apiReference.get.invalidate({ tabId })

	const syncNow = api.apiReference.syncNow.useMutation({
		onSuccess: (result) => {
			syncToast(result)
			void refresh()
		},
		onError: (error) =>
			toast.error({ title: "Sync failed", description: error.message }),
	})

	const data = query.data
	if (query.isLoading) {
		// The header stays so the tabs in it hold still while the spec loads.
		return (
			<div className="flex h-full flex-col">
				<div className={API_PANE_HEADER_CLASS}>
					<DocsHeaderTabs />
				</div>
				<div className="flex min-h-0 flex-1 items-center justify-center">
					<Loader size={10} />
				</div>
			</div>
		)
	}
	if (!data) {
		return (
			<div className="p-8 text-sm text-tertiary">
				This API reference no longer exists.
			</div>
		)
	}
	const state = data.state
	const liveUrl =
		workspace && data.slug
			? getPortalUrl(workspace, `/docs/${data.slug}`, portalSlug)
			: null

	return (
		<div className={cn("flex h-full flex-col", API_MONO_CLASS)}>
			<div className={API_PANE_HEADER_CLASS}>
				<DocsHeaderTabs />
				<div className="mr-auto sm:hidden">
					<MobileBackButton to="/edit-docs" label="Back to docs" />
				</div>
				<RoleCheck not={["VIEWER"]}>
					{state?.source.type === "url" && (
						<Button
							variant="outline"
							size="sm"
							Icon={Sync}
							isLoading={syncNow.isLoading}
							onClick={() => syncNow.mutate({ tabId })}
						>
							Sync now
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						Icon={Gear}
						onClick={() => setApiSettingsTabId(tabId)}
					>
						Settings
					</Button>
					{tab && !tab.isPublished && (
						<Button
							variant="default"
							size="sm"
							Icon={Eye}
							isLoading={isPublishing}
							onClick={() => {
								setIsPublishing(true)
								void publishTab(tab, true).finally(() => setIsPublishing(false))
							}}
						>
							{TAB_COPY.publish}
						</Button>
					)}
				</RoleCheck>
				{/* The live page 404s until the tab is published. */}
				{liveUrl && tab?.isPublished && (
					<Button
						variant="outline"
						size="sm"
						Icon={External}
						onClick={() => window.open(liveUrl, "_blank", "noopener")}
					>
						View live
					</Button>
				)}
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto">
				<div
					className={cn(
						"mx-auto flex max-w-3xl flex-col gap-8 px-6 pb-16 pt-10 sm:px-10",
						MOBILE_BAR_CLEARANCE,
					)}
				>
					<header className="flex flex-col gap-5">
						<h1 className="text-2xl font-semibold tracking-[-0.02em] text-primary">
							{data.name}
						</h1>
						{state && <SourceRow state={state} />}
					</header>

					{state?.held && (
						<HeldBanner tabId={tabId} held={state.held} onDone={refresh} />
					)}
					{!state?.held && state?.lastError && (
						<FailedBanner
							error={state.lastError}
							isSyncing={syncNow.isLoading}
							onSync={() => syncNow.mutate({ tabId })}
							onEditSource={() => setApiSettingsTabId(tabId)}
						/>
					)}

					<EndpointsSection data={data} onSaved={refresh} />
				</div>
			</div>
		</div>
	)
})

/** Where the spec comes from and when it was last checked, as one quiet row. */
function SourceRow({ state }: { state: NonNullable<ApiReference["state"]> }) {
	const source = state.source
	const url = source.type === "url" ? splitUrl(source.url) : null
	return (
		// A phone gives the URL its own line and puts the check time under it.
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm sm:h-11 sm:flex-nowrap sm:py-0 dark:border-gray-700/80">
			<span className="flex min-w-0 flex-1 basis-full items-center gap-3 sm:basis-0">
				{source.type === "url" ? (
					<Link3 className="size-4 flex-none text-tertiary" />
				) : (
					<FileIcon className="size-4 flex-none text-tertiary" />
				)}
				<span className="min-w-0 flex-1 truncate">
					{url ? (
						<>
							<span className="text-primary">{url.host}</span>
							<span className="text-tertiary">{url.rest}</span>
						</>
					) : (
						<span className="text-primary">
							{source.type === "file" ? source.fileName : ""}
						</span>
					)}
				</span>
			</span>
			<span className="flex flex-none items-center gap-3 pl-7 sm:pl-0">
				<span className="text-[13px] text-tertiary">
					Checked {relative(state.lastCheckedAt)}
				</span>
				{source.type === "url" && state.autoSync && (
					<Badge variant="light-gray" size="sm" className="flex-none">
						Daily sync
					</Badge>
				)}
			</span>
		</div>
	)
}

export function splitUrl(raw: string): { host: string; rest: string } {
	try {
		const url = new URL(raw)
		const rest = `${url.pathname === "/" ? "" : url.pathname}${url.search}`
		return { host: url.host, rest }
	} catch {
		return { host: raw, rest: "" }
	}
}

function HeldBanner({
	tabId,
	held,
	onDone,
}: {
	tabId: string
	held: NonNullable<NonNullable<ApiReference["state"]>["held"]>
	onDone: () => void
}) {
	const publish = api.apiReference.publishHeld.useMutation({
		onSuccess: (result) => {
			if (result.status === "failed") {
				toast.error({ title: "Couldn't publish", description: result.error })
			} else {
				toast.success({ title: "Published the held changes" })
			}
			onDone()
		},
	})
	const dismiss = api.apiReference.dismissHeld.useMutation({
		onSuccess: onDone,
	})
	const [showAll, setShowAll] = useState(false)
	const lines = showAll ? held.lines : held.lines.slice(0, 6)

	return (
		<div className="overflow-hidden rounded-xl bg-amber-50 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
			<div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
				<div className="flex min-w-0 flex-col gap-0.5">
					<span className="text-sm font-medium text-amber-900 dark:text-amber-200">
						{`Held back ${held.breakingCount} breaking change${held.breakingCount === 1 ? "" : "s"} ${relative(held.at)}`}
					</span>
					<span className="text-sm text-amber-800 dark:text-amber-300/80">
						{held.largeRemoval
							? "More than a fifth of the endpoints disappeared. Readers still see the previous version."
							: "Readers still see the previous version until you publish."}
					</span>
				</div>
				<RoleCheck not={["VIEWER"]}>
					<div className="flex flex-none gap-2">
						<Button
							variant="outline"
							isLoading={dismiss.isLoading}
							onClick={() => dismiss.mutate({ tabId })}
						>
							Dismiss
						</Button>
						<Button
							variant="default"
							isLoading={publish.isLoading}
							onClick={() => publish.mutate({ tabId })}
						>
							Publish anyway
						</Button>
					</div>
				</RoleCheck>
			</div>
			<ul className="divide-y divide-amber-200/70 bg-white dark:divide-amber-500/20 dark:bg-gray-900">
				{lines.map((line, index) => (
					<li key={index} className="flex items-start gap-3 px-4 py-2 text-sm">
						<span
							className={cn(
								"w-11 flex-none pt-px font-mono text-[11px] font-medium",
								METHOD_TEXT[
									line.method ? apiMethodLabel(line.method, "endpoint") : ""
								] ?? "text-tertiary",
							)}
						>
							{line.path
								? apiMethodLabel(
										line.method ?? "",
										line.method ? "endpoint" : "webhook",
									)
								: "SPEC"}
						</span>
						<div className="flex min-w-0 flex-1 flex-col">
							{line.path && (
								<span className="truncate font-mono text-xs text-primary">
									{line.path}
								</span>
							)}
							<span className="text-secondary">{line.text}</span>
						</div>
						{line.breaking && (
							<Badge variant="amber" size="sm" className="flex-none">
								Breaking
							</Badge>
						)}
					</li>
				))}
			</ul>
			{held.lines.length > 6 && (
				<button
					type="button"
					onClick={() => setShowAll((value) => !value)}
					className="w-full bg-white px-4 py-2 text-left text-sm text-secondary hover:text-primary dark:bg-gray-900"
				>
					{showAll ? "Show less" : `Show all ${held.lines.length} changes`}
				</button>
			)}
		</div>
	)
}

function FailedBanner({
	error,
	isSyncing,
	onSync,
	onEditSource,
}: {
	error: string
	isSyncing: boolean
	onSync: () => void
	onEditSource: () => void
}) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-200 dark:bg-red-500/10 dark:ring-red-500/30">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="text-sm font-medium text-red-900 dark:text-red-200">
					The last sync failed
				</span>
				<span className="text-sm text-red-800 dark:text-red-300/80">
					{error} Readers still see the last published version.
				</span>
			</div>
			<RoleCheck not={["VIEWER"]}>
				<div className="flex flex-none gap-2">
					<Button variant="outline" onClick={onEditSource}>
						Edit source
					</Button>
					<Button variant="default" isLoading={isSyncing} onClick={onSync}>
						Sync now
					</Button>
				</div>
			</RoleCheck>
		</div>
	)
}

function EndpointsSection({
	data,
	onSaved,
}: {
	data: ApiReference
	onSaved: () => void
}) {
	const canEdit = useCanEdit()
	const navigate = useNavigate()
	const update = api.apiReference.update.useMutation({ onSuccess: onSaved })
	const [hidden, setHidden] = useState<Set<string>>(
		() => new Set(data.state?.hidden ?? []),
	)
	useEffect(() => {
		setHidden(new Set(data.state?.hidden ?? []))
	}, [data.state?.hidden])

	const save = (next: Set<string>) => {
		const previous = hidden
		setHidden(next)
		update.mutate(
			{ tabId: data.id, hidden: [...next] },
			{
				onError: (error) => {
					setHidden(previous)
					toast.error({ title: "Couldn't save", description: error.message })
				},
			},
		)
	}
	const toggle = (keys: string[], visible: boolean) => {
		const next = new Set(hidden)
		for (const key of keys) {
			if (visible) next.delete(key)
			else next.add(key)
		}
		save(next)
	}

	if (data.groups.length === 0) {
		return <p className="text-sm text-tertiary">No published version yet.</p>
	}

	const total = data.groups.reduce((sum, group) => sum + group.items.length, 0)
	const hiddenCount = data.groups.reduce(
		(sum, group) =>
			sum + group.items.filter((item) => hidden.has(item.key)).length,
		0,
	)

	return (
		<section className="flex flex-col gap-3">
			<div className="flex items-baseline justify-between gap-3">
				<h2 className="text-base font-semibold text-primary">Pages</h2>
				<span className="text-[13px] tabular-nums text-tertiary">
					{total - hiddenCount} of {total} shown
				</span>
			</div>
			{data.groups.map((group) => {
				const keys = group.items.map((item) => item.key)
				const shown = keys.filter((key) => !hidden.has(key)).length
				return (
					<div
						key={group.slug}
						className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700/80"
					>
						<div className="flex h-10 items-center gap-3 border-b border-gray-200 bg-gray-50 px-3 text-sm dark:border-gray-700/80 dark:bg-gray-800/40">
							<Checkbox
								size="sm"
								aria-label={`Show every page in ${group.name ?? "Webhook events"}`}
								checked={
									shown === keys.length
										? true
										: shown === 0
											? false
											: "indeterminate"
								}
								disabled={!canEdit}
								onCheckedChange={(checked) => toggle(keys, checked !== false)}
							/>
							<span className="min-w-0 flex-1 truncate font-medium text-primary">
								{group.kind === "webhook" ? "Webhook events" : group.name}
							</span>
							<span className="flex-none text-[13px] tabular-nums text-tertiary">
								{shown} of {keys.length}
							</span>
						</div>
						<ul className="divide-y divide-gray-200 dark:divide-gray-700/80">
							{group.items.map((item) => {
								const visible = !hidden.has(item.key)
								return (
									<li
										key={item.key}
										className="flex h-10 items-center gap-3 px-3 text-sm"
									>
										<Checkbox
											size="sm"
											aria-label={`Show ${item.title}`}
											checked={visible}
											disabled={!canEdit}
											onCheckedChange={(checked) =>
												toggle([item.key], checked === true)
											}
										/>
										<button
											type="button"
											onClick={() =>
												void navigate({
													to: "/edit-docs/api/$tabId/$groupSlug/$slug",
													params: {
														tabId: data.id,
														groupSlug: group.slug,
														slug: item.slug,
													},
												})
											}
											className="flex min-w-0 flex-1 cursor-default items-center gap-3 text-left"
										>
											<ApiMethodBadge
												method={item.method}
												kind={item.kind}
												className={cn(
													"w-12 flex-none justify-center",
													!visible && "opacity-50",
												)}
											/>
											<span
												className={cn(
													"min-w-0 flex-1 truncate hover:underline",
													visible ? "text-primary" : "text-tertiary",
												)}
											>
												{item.title}
											</span>
											<span className="hidden min-w-0 max-w-[45%] truncate font-mono text-[12px] text-tertiary sm:block">
												{item.path}
											</span>
										</button>
									</li>
								)
							})}
						</ul>
					</div>
				)
			})}
		</section>
	)
}
